<script setup>
import { reactive, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  activity: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

const ACTIVITY_TYPE_OPTIONS = [
  { value: 'call', label: 'ACTIVITIES.FORM.TYPE.OPTIONS.CALL' },
  { value: 'email', label: 'ACTIVITIES.FORM.TYPE.OPTIONS.EMAIL' },
  { value: 'meeting', label: 'ACTIVITIES.FORM.TYPE.OPTIONS.MEETING' },
  { value: 'visit', label: 'ACTIVITIES.FORM.TYPE.OPTIONS.VISIT' },
  { value: 'other', label: 'ACTIVITIES.FORM.TYPE.OPTIONS.OTHER' },
];

const timestampToLocalInput = ts => {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  const offset = d.getTimezoneOffset();
  const local = new Date(d.getTime() - offset * 60 * 1000);
  return local.toISOString().slice(0, 16);
};

const localInputToTimestamp = str => {
  if (!str) return null;
  return Math.floor(new Date(str).getTime() / 1000);
};

const initialState = () => ({
  title: '',
  description: '',
  activityType: 'call',
  dueDate: '',
  reminderAt: '',
  assigneeId: '',
  contactId: '',
  dealId: '',
});

const form = reactive(initialState());

const isEdit = computed(() => !!props.activity);

const isFormInvalid = computed(() => !form.title.trim() || !form.dueDate);

const typeTooltip = computed(() => {
  return [
    `${t('ACTIVITIES.TYPE.CALL')}: ${t('ACTIVITIES.TYPE.DESCRIPTION.CALL')}`,
    `${t('ACTIVITIES.TYPE.EMAIL')}: ${t('ACTIVITIES.TYPE.DESCRIPTION.EMAIL')}`,
    `${t('ACTIVITIES.TYPE.MEETING')}: ${t('ACTIVITIES.TYPE.DESCRIPTION.MEETING')}`,
    `${t('ACTIVITIES.TYPE.VISIT')}: ${t('ACTIVITIES.TYPE.DESCRIPTION.VISIT')}`,
    `${t('ACTIVITIES.TYPE.OTHER')}: ${t('ACTIVITIES.TYPE.DESCRIPTION.OTHER')}`,
  ].join('\n');
});

const populateForm = activity => {
  if (!activity) return;
  form.title = activity.title || '';
  form.description = activity.description || '';
  form.activityType = activity.activityType || 'call';
  form.dueDate = timestampToLocalInput(activity.dueDate);
  form.reminderAt = timestampToLocalInput(activity.reminderAt);
  form.assigneeId = activity.assigneeId?.toString() || '';
  form.contactId = activity.contactId?.toString() || '';
  form.dealId = activity.dealId?.toString() || '';
};

watch(
  () => props.activity,
  activity => {
    if (activity) populateForm(activity);
  },
  { immediate: true }
);

const handleSubmit = () => {
  if (isFormInvalid.value) return;
  const data = {
    title: form.title.trim(),
    description: form.description.trim(),
    activityType: form.activityType,
    dueDate: localInputToTimestamp(form.dueDate),
    reminderAt: localInputToTimestamp(form.reminderAt),
    assigneeId: form.assigneeId ? Number(form.assigneeId) : null,
    contactId: form.contactId ? Number(form.contactId) : null,
    dealId: form.dealId ? Number(form.dealId) : null,
  };
  emit('submit', data);
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50"
    @click.self="handleClose"
  >
    <div
      class="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
    >
      <div
        class="flex items-center justify-between p-5 border-b border-slate-200"
      >
        <h3 class="text-base font-medium text-slate-900">
          {{
            isEdit
              ? t('ACTIVITIES.FORM.TITLE.EDIT')
              : t('ACTIVITIES.FORM.TITLE.CREATE')
          }}
        </h3>
        <button
          type="button"
          class="text-slate-400 hover:text-slate-600 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5" />
        </button>
      </div>
      <form class="flex flex-col gap-4 p-5" @submit.prevent="handleSubmit">
        <div class="flex flex-col gap-1.5">
          <label
            for="activity-title"
            class="text-sm font-medium text-slate-700"
          >
            {{ t('ACTIVITIES.FORM.TITLE.LABEL') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            id="activity-title"
            v-model="form.title"
            type="text"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
            :placeholder="t('ACTIVITIES.FORM.TITLE.PLACEHOLDER')"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label
            for="activity-description"
            class="text-sm font-medium text-slate-700"
          >
            {{ t('ACTIVITIES.FORM.DESCRIPTION.LABEL') }}
          </label>
          <textarea
            id="activity-description"
            v-model="form.description"
            rows="3"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors resize-none"
            :placeholder="t('ACTIVITIES.FORM.DESCRIPTION.PLACEHOLDER')"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="activity-type" class="text-sm font-medium text-slate-700">
            <span class="inline-flex items-center gap-1">
              {{ t('ACTIVITIES.FORM.TYPE.LABEL') }}
              <i
                class="i-lucide-info size-3.5 text-slate-400"
                :title="typeTooltip"
              />
            </span>
          </label>
          <select
            id="activity-type"
            v-model="form.activityType"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors bg-white"
          >
            <option
              v-for="option in ACTIVITY_TYPE_OPTIONS"
              :key="option.value"
              :value="option.value"
            >
              {{ t(option.label) }}
            </option>
          </select>
        </div>

        <div class="flex flex-col gap-1.5">
          <label
            for="activity-due-date"
            class="text-sm font-medium text-slate-700"
          >
            {{ t('ACTIVITIES.FORM.DUE_DATE.LABEL') }}
            <span class="text-red-500">*</span>
          </label>
          <input
            id="activity-due-date"
            v-model="form.dueDate"
            type="datetime-local"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
          />
        </div>

        <div class="flex flex-col gap-1.5">
          <label
            for="activity-reminder"
            class="text-sm font-medium text-slate-700"
          >
            {{ t('ACTIVITIES.FORM.REMINDER.LABEL') }}
          </label>
          <input
            id="activity-reminder"
            v-model="form.reminderAt"
            type="datetime-local"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
          />
        </div>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div class="flex flex-col gap-1.5">
            <label
              for="activity-assignee"
              class="text-sm font-medium text-slate-700"
            >
              {{ t('ACTIVITIES.FORM.ASSIGNEE.LABEL') }}
            </label>
            <input
              id="activity-assignee"
              v-model="form.assigneeId"
              type="text"
              class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
              :placeholder="t('ACTIVITIES.FORM.ASSIGNEE.PLACEHOLDER')"
            />
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              for="activity-contact"
              class="text-sm font-medium text-slate-700"
            >
              {{ t('ACTIVITIES.FORM.CONTACT.LABEL') }}
            </label>
            <input
              id="activity-contact"
              v-model="form.contactId"
              type="text"
              class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
              :placeholder="t('ACTIVITIES.FORM.CONTACT.PLACEHOLDER')"
            />
          </div>
        </div>

        <div class="flex flex-col gap-1.5">
          <label for="activity-deal" class="text-sm font-medium text-slate-700">
            {{ t('ACTIVITIES.FORM.DEAL.LABEL') }}
          </label>
          <input
            id="activity-deal"
            v-model="form.dealId"
            type="text"
            class="px-3 py-2 text-sm rounded-md border border-slate-300 focus:border-woot-500 focus:ring-1 focus:ring-woot-500 outline-none transition-colors"
            :placeholder="t('ACTIVITIES.FORM.DEAL.PLACEHOLDER')"
          />
        </div>

        <div class="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            class="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-md transition-colors"
            @click="handleClose"
          >
            {{ t('ACTIVITIES.FORM.CANCEL') }}
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-woot-500 hover:bg-woot-600 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="isFormInvalid"
          >
            {{ t('ACTIVITIES.FORM.SAVE') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>
